<script setup lang="ts">
/**
 * Singapura — AR Active Declaration Statement (CSI002). Studio panel: 단일 문서 + create.
 *   cdd/CddVarField/useCddFill 는 aura/agm 재사용. 반복부·서명자 없음(물리 서명).
 *   회사·FYE + 서명 이사(첫 이사) 자동채움.
 */
import { ref, computed, watch, onMounted, provide, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail } from '@/features/AI-documents/types/docGen.types';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import { provideAgmFill, bareKey, typeOf } from '../../aura/agm/cdd/useCddFill';
import ActiveDeclarationPackageDoc from '../active-declaration/ActiveDeclarationPackageDoc.vue';
import { normalizeSingapuraActiveDeclarationForWord } from '../utils/normalizeSingapuraActiveDeclarationForWord';
import { toLongDate } from '../utils/longDate';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { entityMyApi } from '@/features/Entity_my/api/entityApi';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  appliedAddress?: { key: string; line?: string; line1?: string; line2?: string; line3?: string; town?: string; state?: string; postcode?: string; country?: string; seq?: number } | null;
  editTaskId?: number | null;
  studio?: boolean;
  chatFormData?: Record<string, any> | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: Record<string, any>): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-address-input', key: string): void;
}>();

const router = useRouter();
const fill = provideAgmFill({ formatDate: toLongDate }); // 화면 날짜도 "30 June 2026" (고객 요청 2026-10-15)
const autofilledKeys = ref<Set<string>>(new Set());
const lastAutofillCd = ref('');
const taskConfigSteps = ref<string[]>([]);
const TASK_CODE = 'CSI002';

const entityCd = computed(() =>
  String(props.entityData?.entityCd || props.entityData?.icpCd || props.chatFormData?.entityCd || props.chatFormData?.icpCd || '').trim(),
);
provide('singapuraActiveDeclEntityCd', entityCd);

const taskTitleLocal = ref('');
const titleModel = computed({ get: () => taskTitleLocal.value || props.taskTitle || '', set: (v: string) => { taskTitleLocal.value = v; } });
const effectiveTitle = computed(() => titleModel.value.trim());
const studio = computed(() => !!props.studio);
const editMode = computed(() => !!props.editTaskId);
const creating = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; presetProgress.message = v ? progress.value || 'Creating task…' : ''; });
watch(progress, (m) => { if (creating.value) presetProgress.message = m; });

function emitProgress() {
  const total = fill.totalFields.value || 1;
  emit('progress-change', Math.min(100, Math.round((fill.filledCount.value / total) * 100)));
}
watch([() => fill.filledCount.value, () => fill.totalFields.value], () => emitProgress());

const st = (v: unknown) => String(v ?? '').trim();
function isoDate(v: unknown): string { const s = st(v); const m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/); return m ? `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}` : s; }

// person lookup (서명 이사)
fill.setActivateHandler((key: string) => {
  const t = typeOf(key);
  if (t === 'entity') { emit('change-entity'); return; }
  if (t === 'person') {
    emit('request-person-lookup', { fieldGroup: 'singapura-active-decl', key: bareKey(key), scopedKey: key, defaultRole: 'DIRECTOR', lookupType: 'officer', entityCd: entityCd.value });
  }
});
fill.setAddressHandler((key: string) => { emit('request-address-input', key); });
watch(() => props.selectedPerson, (p) => { if (p && fill.activeKey.value) { fill.applyPersonToActive(p as Record<string, any>); emitProgress(); } });

/**
 * 서명 이사 ↔ NRIC 연동.
 *   이사 이름을 다른 이사로 바꾸면 * Nric / Passport / Fin No([[director_id_no]]) 도 그 이사 번호로 바꾼다.
 *   - 직접 입력/수정: 엔티티 이사 목록에서 같은 이름의 번호로
 *   - ✨ 사람 선택: cdd 가 director_identification_no 에 넣는 번호로(이 문서는 director_id_no 를 씀)
 */
const dirIdByName = new Map<string, string>();
watch(() => st(fill.values.director_name), (nm) => {
  const id = dirIdByName.get(nm.toLowerCase());
  if (id !== undefined && st(fill.values.director_id_no) !== id) fill.setValue('director_id_no', id);
});
watch(() => st(fill.values.director_identification_no), (id) => {
  if (id && st(fill.values.director_id_no) !== id) fill.setValue('director_id_no', id);
});

/** 문서에서 반드시 골라야 하는 진술 선택 항목 [키, 안내 라벨] — 비어 있으면 생성 차단 */
const REQUIRED_CHOICES: Array<[string, string]> = [
  ['transfers_status', 'share transfers (registered / not registered / not taken place)'],
  ['transfers_since', 'since (last annual return / incorporation)'],
  ['fs_audited', 'unaudited / audited'],
  ['fs_by_agm', 'annual general meeting / resolution by written means'],
  ['is_solvent', 'able / unable to meet liabilities'],
];

/** Edit 재진입 — 저장값(restoreState)을 복원했으면 엔티티 자동채움이 덮어쓰지 않는다(빈 칸만 채움). */
const restoredFromSaved = ref(false);

// 자동채움 — 회사·FYE + 서명 이사(첫 이사)
async function autofillFromEntity(cdRaw: string) {
  const cd = st(cdRaw);
  if (!cd || cd === lastAutofillCd.value) return;
  if (lastAutofillCd.value && lastAutofillCd.value !== cd) restoredFromSaved.value = false;
  lastAutofillCd.value = cd;
  // ⚠ Edit 재진입: 저장한 서명 이사·NRIC 를 첫 이사로 되돌리지 않는다.
  if (!restoredFromSaved.value) for (const k of autofilledKeys.value) fill.setValue(k, '');
  const written = new Set<string>();
  const set = (k: string, v: unknown) => {
    const s = st(v);
    if (!s) return;
    if (restoredFromSaved.value && st(fill.values[k])) return; // 저장값 보존
    fill.setValue(k, s); written.add(k);
  };
  try {
    const res: any = await fetch(`/api/entity/${encodeURIComponent(cd)}/agm-source`, { credentials: 'include' }).then((r) => r.json());
    const src: any = res?.data?.data ?? res?.data ?? res ?? {};
    set('company_name', src.companyName);
    set('company_uen', src.registrationNo);
    set('financial_year_end', isoDate(src.financialYearEnd));
  } catch (e) { console.warn('[Singapura-Decl] agm-source failed', e); }
  try {
    const res: any = await entityMyApi.getEntityOfficer(cd, { positionGroup: 'director' });
    const off: any = res?.data?.data ?? res?.data ?? res ?? {};
    // 같은 이사가 bizfile 동기화(auto)·수동(manual)에 중복될 수 있어 이름 기준 첫 1건만 남긴다
    const seenDir = new Set<string>();
    const dirs: any[] = [...(off.autoOfficers || []), ...(off.manualOfficers || [])]
      .filter((o) => st(o?.cessationDate) === '' && st(o?.status) !== 'Cessation')
      .filter((o) => { const k = st(o.fullName || o.contactName).toLowerCase(); if (!k || seenDir.has(k)) return false; seenDir.add(k); return true; });
    dirIdByName.clear();
    for (const d of dirs) {
      const nm = st(d.fullName || d.contactName);
      if (nm) dirIdByName.set(nm.toLowerCase(), st(d.contactRegNo));
    }
    const d0 = dirs[0];
    if (d0) { set('director_name', st(d0.fullName || d0.contactName)); set('director_id_no', st(d0.contactRegNo)); }
  } catch (e) { console.warn('[Singapura-Decl] director failed', e); }
  autofilledKeys.value = written;
  emitProgress();
}
watch(() => props.chatFormData, (fd) => {
  if (!fd) return;
  const nm = st(fd.company_name || fd.entityName);
  if (nm && fill.values.company_name !== nm) fill.setValue('company_name', nm);
  const cd = st(fd.entityCd || fd.icpCd);
  if (cd) void autofillFromEntity(cd);
}, { deep: true });
watch(entityCd, (cd) => { if (cd) void autofillFromEntity(cd); }, { immediate: true });

onMounted(async () => {
  emitProgress();
  try { const cfg: any = await getTaskConfigByCode(st(props.selection.task.TASK_CODE) || TASK_CODE); taskConfigSteps.value = Array.isArray(cfg?.processSteps) ? cfg.processSteps : []; } catch { /* ignore */ }
});

function gatherState() { return { values: { ...fill.exportPayload() }, entityCd: entityCd.value, taskTitle: effectiveTitle.value }; }
function restoreState(s: any) { restoredFromSaved.value = true; if (s?.values) for (const [k, v] of Object.entries(s.values)) if (v != null) fill.setValue(k, String(v)); }
function companyFormData() { return normalizeSingapuraActiveDeclarationForWord(fill.exportPayload()); }

const SELECT_PREFIX = 'Please select: ';
const TITLE_MESSAGE = 'Please enter a Task Title.';
const missingChoices = () => REQUIRED_CHOICES.filter(([k]) => !st(fill.values[k])).map(([, label]) => label);
const selectMessage = (missing: string[]) => `${SELECT_PREFIX}${missing.join(' · ')}`;
// 안내 문구는 Create 를 다시 누를 때까지 남아 있었다 — 토글/제목이 바뀌면 바로 다시 계산하고, 다 채우면 지운다.
watch(() => REQUIRED_CHOICES.map(([k]) => fill.values[k]), () => {
  if (!progress.value.startsWith(SELECT_PREFIX)) return;
  const missing = missingChoices();
  progress.value = missing.length ? selectMessage(missing) : '';
});
watch(effectiveTitle, (t) => { if (t && progress.value === TITLE_MESSAGE) progress.value = ''; });
const messageRef = ref<HTMLElement | null>(null);
/** 문서 맨 아래(흐름 안)에 두는 안내 문구로 스크롤 — 문서 위에 겹쳐 띄우지 않는다. */
function revealMessage() { nextTick(() => messageRef.value?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })); }

async function createTask() {
  if (creating.value) return;
  if (!effectiveTitle.value) { progress.value = TITLE_MESSAGE; revealMessage(); return; }
  // 진술 선택 항목을 고르지 않으면 생성하지 않는다. 템플릿 [[if …]]…[[else]] 가 선택 안 함을 else 쪽
  //   (unable / unaudited / written means / have not taken place …)으로 확정해, 사용자가 고르지 않은
  //   법정 진술(예: 채무를 갚을 수 없음)이 문서에 그대로 들어갔다.
  const missing = missingChoices();
  if (missing.length) { progress.value = selectMessage(missing); revealMessage(); return; }
  creating.value = true;
  progress.value = editMode.value ? 'Updating task…' : 'Creating task…';
  try {
    const taskTypeCd = st(props.selection.task.TASK_CODE) || TASK_CODE;
    const companyBase = companyFormData();
    const officerData = { entityCd: entityCd.value || null, icpCd: entityCd.value || null, presetForm: gatherState(), docType: 'SG_AR_ACTIVE_DECL', isMyPreset: props.selection?.isMyPreset === true, formData: companyBase };
    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id'; creating.value = false; return; }
      await fetch(`/api/startTask/tasks/${taskId}/data`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ taskData: { formData: officerData } }) });
      await fetch(`/api/startTask/tasks/${taskId}/title`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ taskTitle: effectiveTitle.value }) });
    } else {
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value || null, taskTypeCd,
          taskTypeName: props.selection.task.TASK_NAME || 'Active Declaration Statement',
          taskTitle: effectiveTitle.value,
          taskFlow: (() => { const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase()); return { createDocument: steps.length === 0 ? true : steps.includes('doc'), requestTSign: steps.includes('t-sign'), proceedWithEFiling: steps.includes('e-filing') }; })(),
          officerData,
        }),
      }).then((r) => r.json());
      taskId = Number(taskRes?.data?.taskId || taskRes?.taskId || 0);
      if (!taskId) { progress.value = `Error creating task: ${taskRes?.error?.message || taskRes?.message || 'no taskId'}`; creating.value = false; return; }
    }
    progress.value = 'Preparing documents…';
    const seen = new Set<number>();
    const docs = (props.selection.documents || []).filter((d: any) => { const tid = Number(d?.tmpId || d?.templateId || 0); if (!tid) return true; if (seen.has(tid)) return false; seen.add(tid); return true; });
    const strip = (s: string) => s.split(' — ')[0].trim();
    const docsToSave = docs.map((d: any) => { const tid = Number(d?.tmpId || d?.templateId || 0); const label = strip(String(d?.templateName || d?.docCode || 'Active Declaration').replace(/\.docx?$/i, '')); return tid ? { templateId: tid, label, personName: st(companyBase.director_name) || st(companyBase.company_name) || 'Company', formData: companyBase } : null; }).filter(Boolean) as Array<{ templateId: number; label: string; personName: string; formData: Record<string, any> }>;
    if (docsToSave.length) {
      try { const r = await fetch('/api/aiDocuments/incorporation/prepare-documents', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }) }).then((x) => x.json()); if (!r?.success) progress.value = `⚠ Documents: ${r?.message || 'unknown'}`; } catch (e: any) { progress.value = `⚠ Document save failed: ${e?.message || e}`; }
    }
    try { await fetch(`/api/startTask/tasks/${taskId}/document-status`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' } }); } catch { /* ignore */ }
    try { await fetch('/api/startTask/preset/use-log', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }) }); } catch { /* ignore */ }
    emit('task-created', { taskId: taskId!, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) { progress.value = e?.message || 'Create failed'; } finally { creating.value = false; }
}

defineExpose({ gatherState, restoreState, createTask });
</script>

<template>
  <div class="sv-panel" :class="{ studio }">
    <div v-if="!studio" class="co-head">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME || 'Active Declaration Statement' }}</div>
        <div class="co-subtitle">Singapura — AR Active Declaration Statement</div>
      </div>
      <div class="co-actions">
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-task-title')">Change title</v-btn>
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-entity')">Change company</v-btn>
      </div>
    </div>
    <div v-if="!studio" class="co-title-row"><v-text-field v-model="titleModel" density="compact" variant="outlined" hide-details label="Task Title" /></div>
    <div class="co-doc"><ActiveDeclarationPackageDoc /></div>
    <div v-if="!studio" class="co-foot">
      <v-btn color="primary" class="text-none" :loading="creating" :disabled="!effectiveTitle || creating" @click="createTask">{{ editMode ? 'Update task' : 'Create Task' }}</v-btn>
      <span v-if="progress && !creating" ref="messageRef" class="co-err">{{ progress }}</span>
    </div>
    <div v-else-if="progress && !creating" ref="messageRef" class="co-foot co-msg"><span class="co-err">{{ progress }}</span></div>
  </div>
</template>

<style scoped>
.sv-panel { display: flex; flex-direction: column; gap: 12px; min-height: 0; }
/* studio: 안내 문구는 문서 아래 흐름 안에 둔다(sticky 로 띄우면 서명란 Designation · NRIC 줄을 가렸다) */
.sv-panel.studio .co-msg { padding: 8px 12px; border: 1px solid #f3c9c4; border-radius: 8px; background: #fdf3f2; }
.co-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.co-title { font-size: 16px; font-weight: 800; color: #1a2336; }
.co-subtitle { font-size: 12px; color: #6b7a94; margin-top: 2px; }
.co-actions { display: flex; gap: 4px; flex-shrink: 0; }
.co-title-row { max-width: 520px; }
.co-doc { flex: 1; min-height: 0; }
.co-foot { display: flex; align-items: center; gap: 12px; }
.co-err { font-size: 12px; color: #c0392b; }
</style>
